import { useCallback, useEffect } from 'react';

import { createSearchMiss, updateSearchMissOutcome } from '../../../util/api';
import {
  captureSearchAttribution,
  createSearchMissEventId,
  getSearchMissDevice,
  searchMissOutcomeEventName,
} from '../../../util/searchMiss';

// This entry intentionally survives React development remounts. A hook-local ref is recreated
// during a full Strict Mode remount and would otherwise log the same rendered search twice.
let activeSearchMissEntry = null;

const settleSearchMiss = (entry, outcome) => {
  if (!entry || entry.settled) return;
  entry.settled = true;

  entry.createPromise
    .then(created => {
      if (created) return updateSearchMissOutcome(entry.eventId, outcome);
      return null;
    })
    .catch(() => null);
};

/**
 * Log a zero-result render exactly once and expose a first-outcome recorder.
 * Network failures are intentionally silent so analytics never blocks search interactions.
 */
const useSearchMissTracking = props => {
  const {
    authenticated,
    listingsAreLoaded,
    locale,
    location,
    routeListingType,
    totalItems,
  } = props;
  const locationIdentity = `${location.key || 'initial'}:${location.pathname}${location.search}`;
  const shouldTrack = listingsAreLoaded && totalItems === 0;

  useEffect(() => {
    const handleOutcome = event => settleSearchMiss(activeSearchMissEntry, event.detail?.outcome);
    window.addEventListener(searchMissOutcomeEventName, handleOutcome);
    return () => {
      window.removeEventListener(searchMissOutcomeEventName, handleOutcome);
      const activeEntry = activeSearchMissEntry;
      const browserSearchPath = `${window.location.pathname}${window.location.search}`;
      if (activeEntry && browserSearchPath !== activeEntry.searchPath) {
        if (window.location.pathname.startsWith('/s')) {
          settleSearchMiss(activeEntry, 'searched_again');
        }
        activeSearchMissEntry = null;
      }
    };
  }, []);

  useEffect(() => {
    const activeEntry = activeSearchMissEntry;
    if (activeEntry && activeEntry.locationIdentity !== locationIdentity) {
      settleSearchMiss(activeEntry, 'searched_again');
      activeSearchMissEntry = null;
    }

    if (!shouldTrack || activeSearchMissEntry?.locationIdentity === locationIdentity) return;

    const attribution = captureSearchAttribution();
    const eventId = createSearchMissEventId();
    const entry = {
      eventId,
      locationIdentity,
      searchPath: `${location.pathname}${location.search}`,
      settled: false,
      createPromise: null,
    };
    entry.createPromise = createSearchMiss({
      eventId,
      authenticated,
      queryString: location.search || '',
      routeListingType,
      locale,
      referrer: attribution.referrer,
      utm: attribution.utm,
      device: getSearchMissDevice(),
    }).catch(() => null);
    activeSearchMissEntry = entry;
  }, [authenticated, locale, location.search, locationIdentity, routeListingType, shouldTrack]);

  return useCallback(outcome => {
    settleSearchMiss(activeSearchMissEntry, outcome);
  }, []);
};

export default useSearchMissTracking;
